import {computed, inject, Injectable, signal} from '@angular/core';
import {catchError, from, Observable, of, switchMap} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {WebPushRepositoryService} from '../repositories/web-push-repository/web-push-repository.service';
import {WebSocketService} from './websocket.service';

/**
 * WebPushService
 *
 * Manages browser push notification lifecycle:
 * - Browser support and permission detection
 * - Push subscription via PushManager (VAPID)
 * - Backend subscription registration and removal
 * - Service worker message handling for subscription rotation
 * - Pusher presence channel subscription for online-presence tracking
 */
@Injectable({
  providedIn: 'root'
})
export class WebPushService {
  private repo = inject(WebPushRepositoryService);
  private webSocket = inject(WebSocketService);

  readonly isSupported = signal<boolean>(false);
  readonly permissionState = signal<NotificationPermission>('default');
  readonly isSubscribed = signal<boolean>(false);

  readonly canSubscribe = computed(() =>
    this.isSupported() && this.permissionState() !== 'denied' && !this.isSubscribed()
  );
  readonly isDenied = computed(() => this.permissionState() === 'denied');

  constructor() {
    if (typeof window !== 'undefined') {
      const supported = 'PushManager' in window && 'serviceWorker' in navigator && 'Notification' in window;
      this.isSupported.set(supported);
      if (supported) {
        this.permissionState.set(Notification.permission);
        this.listenForServiceWorkerMessages();
      }
    }
  }

  /**
   * Enables push notifications:
   * 1. Requests browser notification permission
   * 2. Subscribes to push via PushManager
   * 3. Registers subscription with the backend
   */
  enablePush(): Observable<boolean> {
    return from(this.requestPermission()).pipe(
      switchMap(permission => {
        if (permission !== 'granted') {
          return of(false);
        }
        return this.subscribeBrowserAndRegister();
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Disables push notifications:
   * 1. Unsubscribes browser from push
   * 2. Removes subscription from backend
   */
  disablePush(): Observable<boolean> {
    return from(this.getCurrentBrowserSubscription()).pipe(
      switchMap(subscription => {
        if (!subscription) {
          this.isSubscribed.set(false);
          return of(true);
        }
        const endpoint = subscription.endpoint;
        return from(subscription.unsubscribe()).pipe(
          switchMap(() => this.repo.unsubscribe(endpoint)),
          tap(() => this.isSubscribed.set(false)),
          catchError(() => of(false))
        );
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Boot subscription check.
   * Call on app start when user has opted in (webPushEnabled = true):
   * - If browser has a push subscription, verify it is registered on the server.
   * - If the subscription is missing server-side, re-register it.
   * - If browser has no subscription but permission is granted, re-subscribe.
   * - If permission was revoked, update isSubscribed to false.
   */
  checkAndRefreshSubscription(): Observable<void> {
    return from(this.getCurrentBrowserSubscription()).pipe(
      switchMap(browserSub => {
        if (!browserSub) {
          // No browser subscription
          const permission = Notification.permission;
          this.permissionState.set(permission);
          this.isSubscribed.set(false);
          if (permission === 'granted') {
            // Re-subscribe silently
            return this.subscribeBrowserAndRegister().pipe(map(() => void 0));
          }
          return of(void 0);
        }

        // Have a browser subscription - check if it's on the server
        this.permissionState.set(Notification.permission);
        return this.repo.getSubscriptions().pipe(
          switchMap(serverSubs => {
            const isOnServer = serverSubs.some(s => s.endpoint === browserSub.endpoint && s.isActive);
            if (isOnServer) {
              this.isSubscribed.set(true);
              return of(void 0);
            }
            // Not on server — re-register
            return this.repo.subscribe(browserSub.toJSON()).pipe(
              tap(() => this.isSubscribed.set(true)),
              map(() => void 0)
            );
          }),
          catchError(() => {
            // Leave isSubscribed unchanged — don't assume state on error
            return of(void 0);
          })
        );
      }),
      catchError(() => of(void 0))
    );
  }

  /**
   * Requests browser notification permission.
   * Updates the permissionState signal.
   */
  async requestPermission(): Promise<NotificationPermission> {
    const result = await Notification.requestPermission();
    this.permissionState.set(result);
    return result;
  }

  /**
   * Subscribe to the private Pusher channel for online-presence tracking.
   * The backend checks channel occupancy before sending a push — if occupied,
   * the user is online and the push is suppressed.
   *
   * @param userId - The authenticated user's ID
   */
  subscribeToPresenceChannel(userId: string): void {
    if (userId) {
      this.webSocket.subscribeChannel(`private-user-${userId}`);
    }
  }

  private subscribeBrowserAndRegister(): Observable<boolean> {
    return this.repo.getVapidPublicKey().pipe(
      switchMap(vapidKey => from(this.createBrowserSubscription(vapidKey))),
      switchMap(subscription => this.repo.subscribe(subscription.toJSON())),
      tap(() => this.isSubscribed.set(true)),
      map(() => true),
      catchError(() => of(false))
    );
  }

  private async createBrowserSubscription(vapidKey: string): Promise<PushSubscription> {
    const registration = await navigator.serviceWorker.ready;
    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.urlBase64ToUint8Array(vapidKey)
    });
  }

  private async getCurrentBrowserSubscription(): Promise<PushSubscription | null> {
    if (!this.isSupported()) return null;
    try {
      const registration = await navigator.serviceWorker.ready;
      return registration.pushManager.getSubscription();
    } catch {
      return null;
    }
  }

  private listenForServiceWorkerMessages(): void {
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
        this.handleSubscriptionChanged(event.data);
      }
    });
  }

  private handleSubscriptionChanged(data: {newSubscription: PushSubscriptionJSON | null}): void {
    if (data.newSubscription) {
      // Browser already has a new subscription — just register it with the server
      this.repo.subscribe(data.newSubscription).subscribe({
        next: () => this.isSubscribed.set(true),
        error: () => { /* will be picked up by boot check on next app load */ }
      });
    } else {
      // No new subscription provided — re-subscribe via browser
      this.subscribeBrowserAndRegister().subscribe();
    }
  }

  /**
   * Converts a base64 VAPID key string to a Uint8Array for PushManager.subscribe()
   */
  private urlBase64ToUint8Array(base64String: string): ArrayBuffer {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const bytes = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
      bytes[i] = rawData.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
